import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Buildings, MapPin, Phone, EnvelopeSimple, Globe, VideoCamera, Upload, Trash, DownloadSimple, Play } from "@phosphor-icons/react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Shared « À propos » content — used both:
 *  - inside Settings › tab À propos (admin, isAdmin=true)  → upload/delete allowed
 *  - inside /a-propos standalone page (admin + technicien) → view-only for tech
 *
 * Displays NETKOM company info, MasterTicket version, and an optional MP4 video
 * with a native HTML5 player + download button.
 */
export default function AboutContent({ isAdmin = false }) {
  const [info, setInfo] = useState({ version: "…", has_video: false, video_filename: "", video_size: 0 });
  const [uploading, setUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/settings/about");
      setInfo(r.data || {});
    } catch (e) {
      // If backend not yet reloaded or endpoint missing → silent fallback.
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Le fichier doit être une vidéo");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Vidéo trop volumineuse (max 200 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/about/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Vidéo mise en ligne");
      await load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Supprimer la vidéo de présentation ?")) return;
    try {
      await api.delete("/settings/about/video");
      toast.success("Vidéo supprimée");
      await load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Video streamed via authenticated endpoint. <video> tag can't set headers,
  // so we pass the JWT as a query parameter (backend supports both).
  const token = (() => {
    try { return localStorage.getItem("helios_token") || ""; }
    catch { return ""; }
  })();
  const videoSrc = info.has_video
    ? `${REACT_APP_BACKEND_URL}/api/settings/about/video?token=${encodeURIComponent(token)}&r=${reloadKey}`
    : "";
  const downloadUrl = `${REACT_APP_BACKEND_URL}/api/settings/about/video/download?token=${encodeURIComponent(token)}`;

  const humanSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${(bytes / 1024).toFixed(0)} Ko`;
  };

  return (
    <Card data-testid="settings-about-tab">
      <CardContent className="p-8 md:p-10">
        <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start">
          {/* Logo column */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/masterticket-logo.jpeg"
              alt="MasterTicket — Système de ticketing"
              className="w-full max-w-[240px] rounded-xl shadow-sm border bg-white p-3"
              data-testid="about-logo"
            />
            <div className="text-center">
              <div className="font-display text-xl font-bold tracking-tight">MasterTicket</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">Système de ticketing</div>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[0.7rem] font-medium" data-testid="about-version">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"/> v{info.version}
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className="space-y-6">
            <div>
              <div className="label-caps text-xs">Éditeur du service</div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-1 flex items-center gap-2">
                <Buildings size={26} weight="duotone" className="text-primary"/> NETKOM Informatique
              </h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Société informatique française spécialisée dans les services aux entreprises&nbsp;:
                infogérance, maintenance, hébergement, vente de matériel et développement web.
                Fondée en 2011, NETKOM accompagne PME et indépendants dans leur transformation
                numérique et l'exploitation quotidienne de leur système d'information.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex items-start gap-2.5" data-testid="about-address">
                <MapPin size={18} weight="duotone" className="text-primary mt-0.5 shrink-0"/>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Adresse</div>
                  <div className="text-sm mt-0.5">317 Route de Lyon<br/>69480 Anse — France</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5" data-testid="about-phone">
                <Phone size={18} weight="duotone" className="text-primary mt-0.5 shrink-0"/>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Téléphone</div>
                  <a href="tel:+33474602701" className="text-sm mt-0.5 block hover:text-primary transition-colors">04 74 60 27 01</a>
                </div>
              </div>
              <div className="flex items-start gap-2.5" data-testid="about-email">
                <EnvelopeSimple size={18} weight="duotone" className="text-primary mt-0.5 shrink-0"/>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Email</div>
                  <a href="mailto:support@netkom.fr" className="text-sm mt-0.5 block hover:text-primary transition-colors">support@netkom.fr</a>
                </div>
              </div>
              <div className="flex items-start gap-2.5" data-testid="about-website">
                <Globe size={18} weight="duotone" className="text-primary mt-0.5 shrink-0"/>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Site web</div>
                  <a href="https://www.netkom.fr" target="_blank" rel="noopener noreferrer"
                     className="text-sm mt-0.5 block hover:text-primary transition-colors">
                    www.netkom.fr
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t space-y-2">
              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                <div><span className="text-muted-foreground uppercase tracking-wider">SIREN&nbsp;</span><span className="font-mono">529 224 602</span></div>
                <div><span className="text-muted-foreground uppercase tracking-wider">Code NAF&nbsp;</span><span className="font-mono">6209Z</span></div>
                <div><span className="text-muted-foreground uppercase tracking-wider">Forme&nbsp;</span>SAS</div>
              </div>
              <p className="text-[0.7rem] text-muted-foreground pt-2">
                © {new Date().getFullYear()} NETKOM Informatique — Tous droits réservés.
                MasterTicket est une marque déposée de NETKOM Informatique.
              </p>
            </div>
          </div>
        </div>

        {/* Video section — full width below */}
        <div className="mt-8 pt-8 border-t">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <VideoCamera size={22} weight="duotone" className="text-primary"/>
              <div>
                <div className="font-display text-lg font-bold">Vidéo de présentation</div>
                <div className="text-xs text-muted-foreground">
                  {info.has_video
                    ? <>Fichier : <b>{info.video_filename}</b> {info.video_size ? <span className="text-muted-foreground">({humanSize(info.video_size)})</span> : null}</>
                    : <>Aucune vidéo pour le moment{isAdmin ? " — cliquez sur « Mettre en ligne une vidéo » ci-dessous." : "."}</>}
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-2">
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="video/mp4,video/*"
                    className="hidden"
                    onChange={onUpload}
                    disabled={uploading}
                    data-testid="about-video-upload"
                  />
                  <Button type="button" variant="outline" disabled={uploading} asChild
                    data-testid="about-video-upload-btn">
                    <span className="cursor-pointer">
                      <Upload size={14} className="mr-1.5"/>
                      {uploading ? "Envoi…" : (info.has_video ? "Remplacer la vidéo" : "Mettre en ligne une vidéo")}
                    </span>
                  </Button>
                </label>
                {info.has_video && (
                  <Button type="button" variant="ghost" onClick={onDelete}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="about-video-delete-btn">
                    <Trash size={14} className="mr-1.5"/> Supprimer
                  </Button>
                )}
              </div>
            )}
          </div>

          {info.has_video ? (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden border bg-black shadow-sm">
                <video
                  key={reloadKey}
                  src={videoSrc}
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full aspect-video"
                  data-testid="about-video-player"
                >
                  Votre navigateur ne prend pas en charge la lecture vidéo.
                </video>
              </div>
              <div className="flex justify-end">
                <Button asChild variant="outline" data-testid="about-video-download-btn">
                  <a href={downloadUrl} download={info.video_filename || "masterticket-presentation.mp4"}>
                    <DownloadSimple size={14} className="mr-1.5"/> Télécharger la vidéo
                  </a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed p-10 flex flex-col items-center justify-center text-center bg-muted/30 gap-2">
              <Play size={32} weight="duotone" className="text-muted-foreground/60"/>
              <div className="text-sm text-muted-foreground">
                {isAdmin
                  ? "Aucune vidéo en ligne. Cliquez sur « Mettre en ligne une vidéo » pour partager une présentation avec votre équipe."
                  : "Aucune vidéo de présentation n'est disponible pour le moment."}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
